import React from 'react';
import { Link } from 'react-router-dom';
import LegalPageLayout from './LegalPageLayout';

const PrivacyPolicy = () => {
    return (
        <LegalPageLayout>
            <div className="legal-page">
                <div className="container">
                    <div className="row justify-content-center">
                        <div className="col-12 col-lg-10">
                            <nav aria-label="breadcrumb" className="mt-4">
                                <ol className="breadcrumb">
                                    <li className="breadcrumb-item">
                                        <Link to="/">Home</Link>
                                    </li>
                                    <li className="breadcrumb-item active" aria-current="page">
                                        Privacy Policy
                                    </li>
                                </ol>
                            </nav>

                            <div className="text-center mb-5">
                                <h1 className="h2 font-weight-bold">SalesPinch Privacy Policy</h1>
                                <p className="lead text-muted mb-1">
                                    <strong>Last updated:</strong> April 4, 2026
                                </p>
                                <p className="text-muted mb-0">
                                    <strong>Version:</strong> 1.0
                                </p>
                            </div>

                            <div className="legal-content">
                                <hr className="my-4" />

                                <section className="mb-5">
                                    <h2>1. Introduction and Who We Are</h2>
                                    <p>
                                        SalesPinch (&quot;SalesPinch,&quot; &quot;we,&quot; &quot;our,&quot; or &quot;us&quot;) operates a B2B AI-powered sales CRM platform that helps businesses discover leads, manage sales pipelines, and engage prospects across multiple communication channels including Instagram, Gmail, and others.
                                    </p>
                                    <p>This Privacy Policy explains how we collect, use, disclose, and protect personal information across all of our services. It applies to:</p>
                                    <ul>
                                        <li><strong>Platform Users</strong> — employees and representatives of businesses (&quot;Clients&quot;) who use the SalesPinch platform</li>
                                        <li><strong>End Contacts</strong> — individuals whose information is captured or processed through our Clients&apos; use of the platform (e.g. Instagram users who message a Client&apos;s account, or prospects identified via AI discovery tools)</li>
                                        <li><strong>Website Visitors</strong> — individuals who visit salespinch.com</li>
                                    </ul>
                                    <blockquote>
                                        <p>
                                            <strong>If you are an End Contact whose data has been processed by one of our Clients, please be aware that SalesPinch acts as a data processor on behalf of that Client. For questions about how your data is used by that business specifically, please contact them directly. For deletion requests directed to SalesPinch, see Section 11.</strong>
                                        </p>
                                    </blockquote>
                                </section>

                                <section className="mb-5">
                                    <h2>2. Information We Collect</h2>

                                    <h3>2.1 Information You Provide Directly</h3>
                                    <p>When you register for or use SalesPinch, we collect:</p>
                                    <ul>
                                        <li>Name, email address, phone number, and job title</li>
                                        <li>Company name and business details</li>
                                        <li>Account credentials</li>
                                        <li>Profile preferences and settings</li>
                                        <li>Content you create within the platform (notes, comments, lead descriptions)</li>
                                    </ul>

                                    <h3>2.2 Social Media Message Data (Instagram)</h3>
                                    <p>When a Client connects their Instagram Business Account to SalesPinch, our platform receives and stores:</p>
                                    <ul>
                                        <li><strong>Direct messages (DMs)</strong> sent to or from the Client&apos;s Instagram inbox, including the sender&apos;s Instagram user ID, display name, and message content</li>
                                        <li><strong>Comments</strong> posted on the Client&apos;s Instagram posts, including the commenter&apos;s Instagram user ID, handle, and comment text</li>
                                        <li><strong>Message metadata</strong> such as timestamps, media attachments, and thread IDs</li>
                                    </ul>
                                    <p>
                                        This data is received via the Meta Webhooks API and stored in our systems to enable lead detection, AI extraction, and pipeline management features on behalf of the Client. <strong>We process this data under the Client&apos;s authorisation and in accordance with Meta&apos;s platform policies.</strong>
                                    </p>
                                    <p>
                                        Instagram users whose messages or comments are captured should be aware that the business account they contacted may be using a third-party CRM (SalesPinch) to manage communications. This is disclosed to them via the Client&apos;s own privacy notice.
                                    </p>

                                    <h3>2.3 Gmail and Email Integration Data</h3>
                                    <p>When a Platform User connects their Gmail account via OAuth, SalesPinch accesses:</p>
                                    <ul>
                                        <li>The ability to <strong>compose, send, and create email drafts</strong> on behalf of the user</li>
                                        <li><strong>Email address</strong> of the authenticated account</li>
                                        <li>OAuth access tokens and refresh tokens (stored securely and used only to perform authorised actions)</li>
                                    </ul>
                                    <p>
                                        We do <strong>not</strong> read, index, or analyse the content of existing emails in your inbox. We only act on emails you explicitly create or send through the SalesPinch platform.
                                    </p>

                                    <h3>2.4 AI-Processed and Enriched Prospect Data</h3>
                                    <p>SalesPinch uses artificial intelligence to help Clients identify potential leads. This involves:</p>
                                    <ul>
                                        <li><strong>AI Message Extraction</strong> — automated analysis of incoming social messages to detect purchase intent, urgency, and contact information, using Google Vertex AI (Gemini models)</li>
                                        <li><strong>AI Lead Scoring</strong> — assignment of numerical scores (<code>ai_need_score</code>, <code>ai_fit_score</code>, <code>ai_overall_score</code>) to prospects based on inferred fit with a Client&apos;s product</li>
                                        <li>
                                            <strong>Third-Party Data Enrichment</strong> — when a Client runs an AI lead discovery search, we may query the following services to enrich prospect profiles:
                                            <ul>
                                                <li><strong>People Data Labs (PDL)</strong> — professional profile data including name, employer, job title, and location</li>
                                                <li><strong>Hunter.io</strong> — email address discovery and verification</li>
                                                <li><strong>Apollo</strong> — B2B contact and company data</li>
                                            </ul>
                                        </li>
                                    </ul>
                                    <p>
                                        Prospects identified through AI discovery tools may not have directly interacted with SalesPinch or the Client. Their data is sourced from these third-party data providers, who are themselves responsible for the lawful collection of that data. See Section 7 for your rights regarding this data.
                                    </p>

                                    <h3>2.5 Technical and Usage Data</h3>
                                    <p>We automatically collect:</p>
                                    <ul>
                                        <li>IP address and approximate geolocation</li>
                                        <li>Device type, operating system, and browser</li>
                                        <li>Pages visited, features used, and session duration</li>
                                        <li>Push notification tokens (for mobile app users)</li>
                                        <li>Log data for security and debugging purposes</li>
                                    </ul>

                                    <h3>2.6 Meeting and Calendar Data</h3>
                                    <p>When you use meeting integrations (Zoom, Microsoft Teams), we collect:</p>
                                    <ul>
                                        <li>Meeting metadata (title, time, duration, participants)</li>
                                        <li>Meeting URLs and external meeting IDs</li>
                                        <li>Meeting transcripts and minutes of meetings (MoM), where recorded</li>
                                    </ul>
                                </section>

                                <section className="mb-5">
                                    <h2>3. Legal Basis for Processing (GDPR)</h2>
                                    <p>For users and contacts in the European Economic Area (EEA) or United Kingdom, we process personal data on the following legal bases:</p>
                                    <table>
                                        <thead>
                                            <tr>
                                                <th>Processing Activity</th>
                                                <th>Legal Basis</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            <tr>
                                                <td>Providing the SalesPinch platform to Clients</td>
                                                <td><strong>Contract</strong> (Art. 6(1)(b))</td>
                                            </tr>
                                            <tr>
                                                <td>Processing social messages on behalf of a Client</td>
                                                <td><strong>Legitimate interests</strong> of the Client (Art. 6(1)(f))</td>
                                            </tr>
                                            <tr>
                                                <td>Gmail OAuth integration</td>
                                                <td><strong>Consent</strong> (Art. 6(1)(a)) — you explicitly authorise access</td>
                                            </tr>
                                            <tr>
                                                <td>AI-driven lead scoring and extraction</td>
                                                <td><strong>Legitimate interests</strong> (Art. 6(1)(f)) — Clients&apos; interest in identifying prospective customers</td>
                                            </tr>
                                            <tr>
                                                <td>Third-party prospect enrichment (PDL, Hunter, Apollo)</td>
                                                <td><strong>Legitimate interests</strong> (Art. 6(1)(f))</td>
                                            </tr>
                                            <tr>
                                                <td>Security, fraud prevention, and system integrity</td>
                                                <td><strong>Legitimate interests</strong> (Art. 6(1)(f))</td>
                                            </tr>
                                            <tr>
                                                <td>Compliance with legal obligations</td>
                                                <td><strong>Legal obligation</strong> (Art. 6(1)(c))</td>
                                            </tr>
                                            <tr>
                                                <td>Marketing communications (where applicable)</td>
                                                <td><strong>Consent</strong> (Art. 6(1)(a))</td>
                                            </tr>
                                        </tbody>
                                    </table>
                                    <p>
                                        Where we rely on <strong>legitimate interests</strong>, you have the right to object. See Section 7.
                                    </p>
                                </section>

                                <section className="mb-5">
                                    <h2>4. How We Use Your Information</h2>
                                    <p>We use collected information to:</p>
                                    <ul>
                                        <li>Provide, operate, and maintain the SalesPinch platform</li>
                                        <li>Process and display social messages, leads, and pipeline data on behalf of Clients</li>
                                        <li>Perform AI analysis to detect lead intent and score prospects</li>
                                        <li>Enrich prospect profiles using third-party data providers</li>
                                        <li>Send emails and draft communications on behalf of authorised Platform Users</li>
                                        <li>Schedule and manage meetings</li>
                                        <li>Generate analytics, reports, and performance KPIs</li>
                                        <li>Send platform notifications (in-app and push)</li>
                                        <li>Provide customer support</li>
                                        <li>Detect and prevent fraud, abuse, or security incidents</li>
                                        <li>Comply with applicable legal obligations</li>
                                        <li>Improve and develop our platform features</li>
                                    </ul>
                                </section>

                                <section className="mb-5">
                                    <h2>5. AI and Automated Decision-Making</h2>
                                    <p>SalesPinch uses artificial intelligence in the following ways that may affect individuals:</p>

                                    <h3>5.1 AI Message Extraction</h3>
                                    <p>
                                        Incoming Instagram DMs and comments are automatically analysed by AI (Google Vertex AI / Gemini) to determine: whether a message indicates purchase intent, the urgency level, the likely product of interest, and contact details. This analysis is used to create or update lead records in the Client&apos;s CRM.
                                    </p>

                                    <h3>5.2 AI Lead Scoring</h3>
                                    <p>
                                        Prospects identified through discovery tools are automatically assigned scores across three dimensions: need score, fit score, and overall score. These scores influence which prospects are surfaced to Client sales teams.
                                    </p>

                                    <h3>5.3 Your Rights Regarding Automated Processing</h3>
                                    <p>
                                        Under GDPR Article 22, you have the right not to be subject to decisions based solely on automated processing if those decisions produce significant legal or similarly significant effects on you. SalesPinch lead scores are advisory tools used by human sales representatives — a human reviews and acts on all AI-generated assessments. If you have concerns about AI processing of your data, contact us at{' '}
                                        <a href="mailto:admin@mobicloud.ai">admin@mobicloud.ai</a>.
                                    </p>
                                </section>

                                <section className="mb-5">
                                    <h2>6. Information Sharing and Sub-Processors</h2>
                                    <p>We do not sell your personal data. We share data only as follows:</p>

                                    <h3>6.1 Sub-Processors</h3>
                                    <p>We use the following third-party services that process personal data on our behalf:</p>
                                    <table>
                                        <thead>
                                            <tr>
                                                <th>Service</th>
                                                <th>Purpose</th>
                                                <th>Location</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            <tr>
                                                <td><strong>Meta (Facebook/Instagram)</strong></td>
                                                <td>Social message delivery and webhook events</td>
                                                <td>USA</td>
                                            </tr>
                                            <tr>
                                                <td><strong>Google Cloud / Vertex AI</strong></td>
                                                <td>AI message analysis and lead scoring (Gemini models)</td>
                                                <td>Global</td>
                                            </tr>
                                            <tr>
                                                <td><strong>People Data Labs (PDL)</strong></td>
                                                <td>Prospect profile enrichment</td>
                                                <td>USA</td>
                                            </tr>
                                            <tr>
                                                <td><strong>Hunter.io</strong></td>
                                                <td>Email discovery and verification</td>
                                                <td>France</td>
                                            </tr>
                                            <tr>
                                                <td><strong>Apollo</strong></td>
                                                <td>B2B contact enrichment</td>
                                                <td>USA</td>
                                            </tr>
                                            <tr>
                                                <td><strong>Zoom</strong></td>
                                                <td>Meeting creation and management</td>
                                                <td>USA</td>
                                            </tr>
                                            <tr>
                                                <td><strong>Microsoft Teams</strong></td>
                                                <td>Meeting creation and management</td>
                                                <td>USA</td>
                                            </tr>
                                            <tr>
                                                <td><strong>Firebase / Google (FCM/APNs)</strong></td>
                                                <td>Push notifications for mobile app</td>
                                                <td>USA</td>
                                            </tr>
                                            <tr>
                                                <td><strong>Cloud hosting provider</strong></td>
                                                <td>Infrastructure and data storage</td>
                                                <td>USA</td>
                                            </tr>
                                        </tbody>
                                    </table>
                                    <p>We maintain data processing agreements with all sub-processors and require them to process data only as directed by us.</p>

                                    <h3>6.2 Client Access</h3>
                                    <p>Platform data is accessible to the Client organisation that owns the account. SalesPinch does not share one Client&apos;s data with another Client.</p>

                                    <h3>6.3 Legal Requirements</h3>
                                    <p>We may disclose personal data if required by law, court order, or government authority, or to protect the rights, property, or safety of SalesPinch, our Clients, or others.</p>

                                    <h3>6.4 Business Transfers</h3>
                                    <p>In the event of a merger, acquisition, or sale of assets, personal data may be transferred as part of that transaction. We will notify affected users via email or prominent notice on our website.</p>
                                </section>

                                <section className="mb-5">
                                    <h2>7. Your Rights</h2>
                                    <p>Depending on your location, you may have the following rights:</p>

                                    <h3>All Users</h3>
                                    <ul>
                                        <li><strong>Access</strong> — request a copy of the personal data we hold about you</li>
                                        <li><strong>Correction</strong> — request correction of inaccurate or incomplete data</li>
                                        <li><strong>Deletion</strong> — request erasure of your personal data (see our{' '}
                                            <Link to="/data-deletion-policy" className="text-primary">User Data Deletion Policy</Link>).
                                        </li>
                                        <li><strong>Portability</strong> — receive your data in a structured, machine-readable format</li>
                                        <li><strong>Objection</strong> — object to processing based on legitimate interests</li>
                                        <li><strong>Restriction</strong> — request that we limit how we process your data</li>
                                    </ul>

                                    <h3>EEA / UK Users (GDPR)</h3>
                                    <p>All rights above apply. You also have the right to:</p>
                                    <ul>
                                        <li>Withdraw consent at any time (where processing is based on consent)</li>
                                        <li>Lodge a complaint with your national data protection authority (e.g. ICO in the UK, your country&apos;s DPA in the EEA)</li>
                                    </ul>

                                    <h3>California Residents (CCPA/CPRA)</h3>
                                    <ul>
                                        <li><strong>Know</strong> — the categories and specific pieces of personal information collected about you</li>
                                        <li><strong>Delete</strong> — request deletion of personal information</li>
                                        <li><strong>Opt-out of Sale or Sharing</strong> — we do not sell personal information; we do not share it for cross-context behavioural advertising</li>
                                        <li><strong>Non-discrimination</strong> — we will not discriminate against you for exercising your rights</li>
                                        <li><strong>Correct</strong> — request correction of inaccurate personal information</li>
                                        <li><strong>Limit Use of Sensitive Personal Information</strong> — where applicable</li>
                                    </ul>
                                    <p>
                                        To exercise any of these rights, submit a request at:{' '}
                                        <Link to="/data-deletion">salespinch.com/data-deletion</Link>
                                        {' '}or email{' '}
                                        <a href="mailto:admin@mobicloud.ai">admin@mobicloud.ai</a>. We will respond within 30 days (or 45 days where permitted by law for complex requests).
                                    </p>
                                </section>

                                <section className="mb-5">
                                    <h2>8. Data Retention</h2>
                                    <p>We retain personal data for the periods set out below, after which data is securely deleted or anonymised:</p>
                                    <table>
                                        <thead>
                                            <tr>
                                                <th>Data Category</th>
                                                <th>Retention Period</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            <tr>
                                                <td>Platform User account data</td>
                                                <td>Duration of account + 90 days after termination</td>
                                            </tr>
                                            <tr>
                                                <td>Social messages (Instagram DMs/comments)</td>
                                                <td>2 years from capture, or until Client requests deletion</td>
                                            </tr>
                                            <tr>
                                                <td>AI extraction results</td>
                                                <td>2 years from creation</td>
                                            </tr>
                                            <tr>
                                                <td>AI prospect candidates</td>
                                                <td>2 years from creation, or until converted/rejected</td>
                                            </tr>
                                            <tr>
                                                <td>Email drafts and sent log</td>
                                                <td>2 years</td>
                                            </tr>
                                            <tr>
                                                <td>Meeting transcripts and MoM</td>
                                                <td>2 years</td>
                                            </tr>
                                            <tr>
                                                <td>Chat messages (AI assistant)</td>
                                                <td>1 year</td>
                                            </tr>
                                            <tr>
                                                <td>OAuth tokens (Gmail)</td>
                                                <td>Until revoked or account disconnection</td>
                                            </tr>
                                            <tr>
                                                <td>Security and system logs</td>
                                                <td>90 days</td>
                                            </tr>
                                            <tr>
                                                <td>Anonymised analytics</td>
                                                <td>Indefinitely (no personal data retained)</td>
                                            </tr>
                                        </tbody>
                                    </table>
                                    <p>
                                        Where a Client terminates their SalesPinch account, all associated data is scheduled for deletion within 90 days unless a shorter period is required by applicable law.
                                    </p>
                                </section>

                                <section className="mb-5">
                                    <h2>9. Data Security</h2>
                                    <p>We implement the following technical and organisational measures to protect personal data:</p>
                                    <ul>
                                        <li>Encryption of data in transit (TLS) and at rest</li>
                                        <li>OAuth 2.0 for third-party authentication integrations</li>
                                        <li>Access controls with role-based permissions</li>
                                        <li>Secure storage of API keys and credentials (not stored in the database)</li>
                                        <li>Regular security assessments</li>
                                        <li>Incident response procedures with notification obligations</li>
                                    </ul>
                                    <p>
                                        Despite these measures, no system is completely secure. In the event of a data breach affecting your rights and freedoms, we will notify you and relevant authorities as required by law.
                                    </p>
                                </section>

                                <section className="mb-5">
                                    <h2>10. International Data Transfers</h2>
                                    <p>SalesPinch is based in the United States. When we transfer personal data from the EEA, UK, or other regions with data transfer restrictions, we rely on:</p>
                                    <ul>
                                        <li>Standard Contractual Clauses (SCCs) approved by the European Commission</li>
                                        <li>Adequacy decisions where applicable</li>
                                        <li>Other lawful transfer mechanisms</li>
                                    </ul>
                                    <p>Our third-party sub-processors (see Section 6.1) are required to maintain equivalent transfer safeguards.</p>
                                </section>

                                <section className="mb-5">
                                    <h2>11. Data Deletion Requests</h2>
                                    <p>
                                        For full information on how to request deletion of your personal data, what data will be removed, and the timeline for processing, please see our{' '}
                                        <Link to="/data-deletion-policy" className="text-primary"><strong>User Data Deletion Policy</strong></Link>.
                                    </p>
                                    <p>
                                        To submit a deletion request directly:{' '}
                                        <Link to="/data-deletion">salespinch.com/data-deletion</Link>
                                    </p>
                                </section>

                                <section className="mb-5" id="cookie-policy">
                                    <h2>12. Cookies and Tracking</h2>
                                    <p>Our website and platform use cookies and similar technologies for:</p>
                                    <ul>
                                        <li><strong>Essential cookies</strong> — necessary for the platform to function (session management, authentication)</li>
                                        <li><strong>Analytics cookies</strong> — to understand usage patterns and improve the platform</li>
                                        <li><strong>Preference cookies</strong> — to remember your settings</li>
                                    </ul>
                                    <p>
                                        You can manage cookie preferences through your browser settings. Disabling essential cookies will affect platform functionality. For full details, see our{' '}
                                        <Link to="/privacy#cookie-policy" className="text-primary">Cookie Policy</Link>.
                                    </p>
                                </section>

                                <section className="mb-5">
                                    <h2>13. Children&apos;s Privacy</h2>
                                    <p>
                                        SalesPinch is a B2B platform intended for use by businesses and their adult employees. We do not knowingly collect personal information from individuals under the age of 18. If you believe we have inadvertently collected such information, contact us immediately at{' '}
                                        <a href="mailto:admin@mobicloud.ai">admin@mobicloud.ai</a>.
                                    </p>
                                </section>

                                <section className="mb-5">
                                    <h2>14. Meta Platform Policy Compliance</h2>
                                    <p>SalesPinch&apos;s Instagram integration is built on the Meta Business API and complies with Meta&apos;s Platform Terms and Developer Policies. Data received via Meta APIs is:</p>
                                    <ul>
                                        <li>Used only for the purposes disclosed to Meta during App Review</li>
                                        <li>Not used to make eligibility decisions (credit, employment, insurance, etc.)</li>
                                        <li>Not used for surveillance</li>
                                        <li>Not shared with data brokers</li>
                                        <li>Deleted upon Client request or account termination</li>
                                    </ul>
                                    <p>
                                        Users of Instagram who interact with a business using SalesPinch can contact that business directly or submit a request to SalesPinch at{' '}
                                        <a href="mailto:admin@mobicloud.ai">admin@mobicloud.ai</a>.
                                    </p>
                                </section>

                                <section className="mb-5">
                                    <h2>15. Contact Us</h2>
                                    <div className="contact-info">
                                        <p><strong>General Privacy Enquiries:</strong></p>
                                        <p>Email: <a href="mailto:admin@mobicloud.ai">admin@mobicloud.ai</a></p>
                                        <p className="mt-3"><strong>Data Deletion Requests:</strong></p>
                                        <p>Online: <Link to="/data-deletion">salespinch.com/data-deletion</Link></p>
                                        <p>Email: <a href="mailto:admin@mobicloud.ai">admin@mobicloud.ai</a> (Subject: Data Deletion Request)</p>
                                        <p className="mt-3"><strong>Data Protection Officer:</strong></p>
                                        <p>Email: <a href="mailto:admin@mobicloud.ai">admin@mobicloud.ai</a></p>
                                        <p className="mt-3"><strong>Registered Address:</strong></p>
                                        <p>
                                            Plot No 21 Trishul Enclave Balaji Nagar
                                            <br />
                                            Ahmedguda, Hyderabad, Telangana - 501301
                                        </p>
                                    </div>
                                </section>

                                <section className="mb-5">
                                    <h2>16. Changes to This Policy</h2>
                                    <p>We will notify users of material changes to this Privacy Policy by:</p>
                                    <ul>
                                        <li>Email notification to registered Platform Users</li>
                                        <li>A prominent notice on salespinch.com for at least 30 days before changes take effect</li>
                                        <li>Updating the &quot;Last updated&quot; date and version number above</li>
                                    </ul>
                                    <p>Continued use of the platform after changes take effect constitutes acceptance of the updated policy.</p>
                                </section>

                                <p className="text-muted text-center mb-0">
                                    <em>SalesPinch Privacy Policy v1.0 — April 4, 2026</em>
                                </p>
                            </div>

                            <div className="text-center mt-5 pt-4 border-top">
                                <div className="row">
                                    <div className="col-md-3">
                                        <Link to="/terms" className="text-decoration-none">
                                            Terms of Service
                                        </Link>
                                    </div>
                                    <div className="col-md-3">
                                        <Link to="/disclaimer" className="text-decoration-none">
                                            Disclaimer
                                        </Link>
                                    </div>
                                    <div className="col-md-3">
                                        <Link to="/data-deletion" className="text-decoration-none">
                                            Data Deletion Request
                                        </Link>
                                    </div>
                                    <div className="col-md-3">
                                        <Link to="/contact-page" className="text-decoration-none">
                                            Contact Us
                                        </Link>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </LegalPageLayout>
    );
};

export default PrivacyPolicy;
